import { Link } from '@tanstack/react-router';
import { Card, CardContent, PageHeader, formatRelative, Button, Alert } from '@salesbee/ui';
import { Meter, money, OutcomeBadge, QueryView, Section, Stat, useCurrent } from '../components/common';
import { trpc } from '../lib/trpc';

export function DashboardPage() {
  const summary = trpc.dashboard.summary.useQuery();
  const current = useCurrent();
  const onboarding = current.data?.onboarding;
  return (
    <div className="space-y-8">
      <PageHeader title="Dashboard" description={current.data ? `${current.data.name} · ${current.data.entitlements.planName}` : undefined} actions={<Button asChild><Link to="/outbound/campaigns">Campaigns</Link></Button>} />
      {onboarding && !onboarding.complete && !onboarding.dismissed ? (
        <Alert variant="info" title="Finish setting up">
          Add your caller ID, disclosure script and calling hours before the first call. <Link className="font-semibold underline" to="/onboarding">Open the checklist</Link>
        </Alert>
      ) : null}
      <QueryView query={summary} label="Loading dashboard">
        {(s) => (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Open pipeline" value={money(s.pipeline.openValueCents, s.currency)} hint={`${s.pipeline.openDeals} open deals · weighted ${money(s.pipeline.weightedValueCents, s.currency)}`} />
              <Stat label="Calls today" value={s.calls.today} hint={`${s.calls.connectedToday} connected`} />
              <Stat label="Connect rate (30 days)" value={`${Math.round(s.calls.connectRate30d * 100)}%`} hint={`${s.calls.thisMonth} calls this month`} />
              <Stat label="Meetings booked" value={s.calls.meetingsThisMonth} hint="This month" />
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              <Card className="lg:col-span-1">
                <CardContent className="space-y-5 py-6">
                  <h2 className="text-[17px] font-semibold">Plan usage · {s.usage.period}</h2>
                  <Meter label="Voice minutes" used={s.usage.voiceMinutes.used} limit={s.usage.voiceMinutes.limit} unit="min" />
                  <Meter label="Prospects" used={s.usage.prospects.used} limit={s.usage.prospects.limit} unit="" />
                  <p className="text-[13px] text-muted-foreground">Won this month: {money(s.pipeline.wonThisMonthCents, s.currency)} ({s.pipeline.wonThisMonth} deals)</p>
                </CardContent>
              </Card>
              <Card className="lg:col-span-2">
                <CardContent className="space-y-3 py-6">
                  <h2 className="text-[17px] font-semibold">Pipeline by stage</h2>
                  <div className="space-y-2">
                    {s.funnel.map((f) => {
                      const max = Math.max(1, ...s.funnel.map((x) => x.n));
                      return (
                        <div key={f.stageId} className="grid grid-cols-[140px_1fr_120px] items-center gap-3 text-[14px]">
                          <span className="truncate">{f.name}</span>
                          <div className="h-3 rounded-full bg-muted">
                            <div className="h-3 rounded-full" style={{ width: `${(f.n / max) * 100}%`, background: f.color ?? 'var(--color-primary)' }} />
                          </div>
                          <span className="text-right font-mono text-muted-foreground">
                            {f.n} · {money(f.value, s.currency)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Recent calls" actions={<Link className="text-sm underline" to="/outbound/calls">Call log</Link>}>
                <Card>
                  <CardContent className="divide-y divide-border py-2">
                    {s.recentCalls.length === 0 ? <p className="py-4 text-sm text-muted-foreground">No calls yet.</p> : null}
                    {s.recentCalls.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-3 py-3 text-[14px]">
                        <div className="min-w-0">
                          <p className="truncate font-medium">{c.contactName ?? 'Unknown'}</p>
                          <p className="truncate text-muted-foreground">{c.summary ?? (c.mode === 'ai' ? 'AI call' : 'Call')}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <OutcomeBadge outcome={c.outcome} />
                          <span className="text-xs text-muted-foreground">{formatRelative(c.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </Section>
              <Section title="My tasks" actions={<Link className="text-sm underline" to="/crm/tasks">All tasks</Link>}>
                <Card>
                  <CardContent className="divide-y divide-border py-2">
                    {s.dueTasks.length === 0 ? <p className="py-4 text-sm text-muted-foreground">Nothing due this week.</p> : null}
                    {s.dueTasks.map((t) => (
                      <div key={t.id} className="flex items-center justify-between py-3 text-[14px]">
                        <span>{t.title}</span>
                        <span className="text-xs text-muted-foreground">{t.dueAt ? formatRelative(t.dueAt) : ''}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </Section>
            </div>
          </>
        )}
      </QueryView>
    </div>
  );
}
